/**
 * Booked-slot rules shared by every guest date picker.
 *
 * Port of the Flutter app's `lib/widgets/calendar/booked_slot.dart` so web and
 * mobile agree on which days are blocked, which 30-minute chips are disabled
 * and when a pickup/return pair is rejected. All day keys are `yyyy-MM-dd`
 * strings in the listing's IANA zone; instants are UTC milliseconds.
 */
import { DateTime } from 'luxon';
import { outOfOperatingHoursMinutesFor, type CarOperatingHoursRow } from './operatingHours';

export interface BookedSlot {
  startAt: number;
  endAt: number;
}

export interface BlockedDateSets {
  /** Every chip of the day is covered: not selectable. */
  blocked: Set<string>;
  /** Some chips are covered: selectable, drawn struck through with a warning dot. */
  partial: Set<string>;
}

export const MINUTE_STEP = 30;
export const DAY_MINUTES = 24 * 60;
/** Pickup chips earlier than now + this many minutes are disabled on the listing's today. */
export const PICKUP_LEAD_MINUTES = 90;
export const DEFAULT_TIMEZONE = 'America/Los_Angeles';

function toMillis(value: number | string | null | undefined): number {
  if (typeof value === 'number') return value;
  if (value == null || value === '') return NaN;
  const asNumber = Number(value);
  return Number.isNaN(asNumber) ? Date.parse(value) : asNumber;
}

/** `getCarBookedDates.bookedTimeSlots` (numbers or numeric strings) to slots. */
export function slotsFromGraphQl(
  rows: Array<{ startAt: number | string; endAt: number | string }> | null | undefined,
): BookedSlot[] {
  return (rows ?? [])
    .map(row => ({ startAt: toMillis(row.startAt), endAt: toMillis(row.endAt) }))
    .filter(slot => !Number.isNaN(slot.startAt) && !Number.isNaN(slot.endAt));
}

export function dayKeyOf(ms: number, timezone: string): string {
  return DateTime.fromMillis(ms).setZone(timezone).toISODate() || '';
}

export function todayKey(timezone: string, now: number = Date.now()): string {
  return dayKeyOf(now, timezone);
}

export function dayStartInZone(dayKey: string, timezone: string): DateTime {
  return DateTime.fromISO(dayKey, { zone: timezone }).startOf('day');
}

/** Instant for a wall-clock minute offset on a listing-zone day. */
export function instantFor(dayKey: string, minuteOfDay: number, timezone: string): number {
  return dayStartInZone(dayKey, timezone).plus({ minutes: minuteOfDay }).toMillis();
}

export function dayKeysBetween(startKey: string, endKey: string): string[] {
  const out: string[] = [];
  let cursor = DateTime.fromISO(startKey);
  const end = DateTime.fromISO(endKey);
  if (!cursor.isValid || !end.isValid) return out;
  while (cursor <= end) {
    out.push(cursor.toISODate() || '');
    cursor = cursor.plus({ days: 1 });
  }
  return out;
}

/** True when the days (any order) form one unbroken run. */
export function isContiguousSelection(days: string[]): boolean {
  if (days.length < 2) return true;
  const sorted = [...days].sort();
  return dayKeysBetween(sorted[0], sorted[sorted.length - 1]).length === sorted.length;
}

export function addDays(dayKey: string, days: number): string {
  return DateTime.fromISO(dayKey).plus({ days }).toISODate() || dayKey;
}

export function minutesToLabel12h(minuteOfDay: number): string {
  const hour = Math.floor(minuteOfDay / 60) % 24;
  const minute = minuteOfDay % 60;
  const period = hour < 12 ? 'AM' : 'PM';
  return `${hour % 12 || 12}:${String(minute).padStart(2, '0')} ${period}`;
}

/** "2:30 PM" or "14:30" to a minute offset; NaN when unparseable. */
export function labelToMinutes(label: string): number {
  const match = label.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
  if (!match) return NaN;
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3]?.toUpperCase();
  if (period === 'PM' && hour < 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  return hour * 60 + minute;
}

type MinuteRange = { start: number; end: number };

/**
 * Days fully or partly covered by the slots (each widened by the trip buffer on
 * both sides), plus closed and partly closed operating-hours days.
 * Mirrors Flutter `computeBlockedDateSets`.
 */
export function computeBlockedDateSets({
  slots,
  bufferHours = 0,
  fromKey,
  toKey,
  timezone,
  operatingHours = null,
  operatingHoursPartial = false,
  minuteStep = MINUTE_STEP,
}: {
  slots: BookedSlot[];
  bufferHours?: number;
  fromKey: string;
  toKey: string;
  timezone: string;
  operatingHours?: CarOperatingHoursRow | null;
  /**
   * Also mark days that are open only part of the day as partial. Off by default:
   * with ordinary opening hours every day would carry the dot, which hides the
   * booking marks (the Flutter car-details fast path behaves the same way).
   */
  operatingHoursPartial?: boolean;
  minuteStep?: number;
}): BlockedDateSets {
  const blocked = new Set<string>();
  const partial = new Set<string>();
  const stepCount = Math.floor(DAY_MINUTES / minuteStep);
  const windowStart = dayStartInZone(fromKey, timezone);
  const windowEnd = dayStartInZone(toKey, timezone);
  if (!windowStart.isValid || !windowEnd.isValid) return { blocked, partial };

  if (slots.length > 0) {
    const bufferMs = bufferHours * 60 * 60 * 1000;
    const intervalsByDay = new Map<string, MinuteRange[]>();

    slots.forEach(slot => {
      const expandedStart = DateTime.fromMillis(slot.startAt - bufferMs).setZone(timezone);
      const expandedEnd = DateTime.fromMillis(slot.endAt + bufferMs).setZone(timezone);
      if (expandedEnd <= expandedStart) return;

      let day = expandedStart.startOf('day');
      const lastDay = expandedEnd.minus({ milliseconds: 1 }).startOf('day');
      while (day <= lastDay) {
        if (day >= windowStart && day <= windowEnd) {
          const startMin = expandedStart > day ? expandedStart.diff(day, 'minutes').minutes : 0;
          const endMin = Math.min(
            Math.max(expandedEnd.diff(day, 'minutes').minutes, 0),
            DAY_MINUTES,
          );
          if (endMin > startMin) {
            const key = day.toISODate() || '';
            const list = intervalsByDay.get(key) ?? [];
            list.push({ start: startMin, end: endMin });
            intervalsByDay.set(key, list);
          }
        }
        day = day.plus({ days: 1 });
      }
    });

    intervalsByDay.forEach((ranges, key) => {
      ranges.sort((a, b) => a.start - b.start);
      const merged: MinuteRange[] = [];
      ranges.forEach(range => {
        const last = merged[merged.length - 1];
        if (!last || range.start > last.end) merged.push({ ...range });
        else if (range.end > last.end) last.end = range.end;
      });

      let covered = 0;
      let idx = 0;
      for (let minute = 0; minute < DAY_MINUTES; minute += minuteStep) {
        while (idx < merged.length && merged[idx].end <= minute) idx++;
        if (idx < merged.length && merged[idx].start <= minute && minute < merged[idx].end)
          covered++;
      }
      if (covered >= stepCount) blocked.add(key);
      else if (covered > 0) partial.add(key);
    });
  }

  if (operatingHours && !operatingHours.alwaysAvailable) {
    let day = windowStart;
    while (day <= windowEnd) {
      const key = day.toISODate() || '';
      if (!blocked.has(key)) {
        const closed = outOfOperatingHoursMinutesFor(day, operatingHours, minuteStep);
        if (closed.size >= stepCount) {
          partial.delete(key);
          blocked.add(key);
        } else if (operatingHoursPartial && closed.size > 0) {
          partial.add(key);
        }
      }
      day = day.plus({ days: 1 });
    }
  }

  return { blocked, partial };
}

/**
 * Minute offsets on [dayKey] that fall inside a slot widened by the buffer:
 * `[startAt - buffer, endAt + buffer)`. Mirrors Flutter `disabledTimesFor`.
 */
export function disabledTimesFor({
  dayKey,
  slots,
  bufferHours = 0,
  timezone,
  minuteStep = MINUTE_STEP,
}: {
  dayKey: string;
  slots: BookedSlot[];
  bufferHours?: number;
  timezone: string;
  minuteStep?: number;
}): Set<number> {
  const out = new Set<number>();
  if (slots.length === 0) return out;
  const bufferMs = bufferHours * 60 * 60 * 1000;
  const dayStart = dayStartInZone(dayKey, timezone);
  for (let minute = 0; minute < DAY_MINUTES; minute += minuteStep) {
    const candidate = dayStart.plus({ minutes: minute }).toMillis();
    const hit = slots.some(
      slot => candidate >= slot.startAt - bufferMs && candidate < slot.endAt + bufferMs,
    );
    if (hit) out.add(minute);
  }
  return out;
}

/** Minute offsets outside operating hours on [dayKey]; empty without hours. */
export function operatingHoursDisabledTimesFor({
  dayKey,
  timezone,
  operatingHours,
  minuteStep = MINUTE_STEP,
}: {
  dayKey: string;
  timezone: string;
  operatingHours?: CarOperatingHoursRow | null;
  minuteStep?: number;
}): Set<number> {
  if (!operatingHours || operatingHours.alwaysAvailable) return new Set();
  return outOfOperatingHoursMinutesFor(
    dayStartInZone(dayKey, timezone),
    operatingHours,
    minuteStep,
  );
}

/**
 * Pickup chips earlier than now + lead on the listing's today. Empty on any
 * other day. Mirrors Flutter `pickupChipIsDisabledForListingTodayLead`.
 */
export function pickupLeadDisabledTimes({
  dayKey,
  timezone,
  leadMinutes = PICKUP_LEAD_MINUTES,
  now = Date.now(),
  minuteStep = MINUTE_STEP,
}: {
  dayKey: string;
  timezone: string;
  leadMinutes?: number;
  now?: number;
  minuteStep?: number;
}): Set<number> {
  const out = new Set<number>();
  if (dayKey !== todayKey(timezone, now)) return out;
  const earliest = now + leadMinutes * 60 * 1000;
  const dayStart = dayStartInZone(dayKey, timezone);
  for (let minute = 0; minute < DAY_MINUTES; minute += minuteStep) {
    if (dayStart.plus({ minutes: minute }).toMillis() < earliest) out.add(minute);
  }
  return out;
}

/** First chip not in [disabled] at or after [fromMinute]; null when none. */
export function firstSelectableMinute(
  disabled: Set<number>,
  fromMinute = 0,
  minuteStep = MINUTE_STEP,
): number | null {
  for (let minute = fromMinute; minute < DAY_MINUTES; minute += minuteStep) {
    if (!disabled.has(minute)) return minute;
  }
  return null;
}

function formatInstant(ms: number, timezone: string): string {
  return DateTime.fromMillis(ms).setZone(timezone).toFormat('MMM d, h:mm a');
}

/**
 * Null when the pair is bookable, else the message to show. Order and wording
 * mirror Flutter `validateRentalDateRange`: ordering, booking overlap (with
 * buffer), a fully blocked day inside the trip, then operating hours.
 */
export function validateRentalDateRange({
  startMs,
  endMs,
  slots,
  bufferHours = 0,
  timezone,
  operatingHours = null,
  blocked,
  minuteStep = MINUTE_STEP,
}: {
  startMs: number;
  endMs: number;
  slots: BookedSlot[];
  bufferHours?: number;
  timezone: string;
  operatingHours?: CarOperatingHoursRow | null;
  /** Precomputed fully blocked days; when omitted the slots decide. */
  blocked?: Set<string>;
  minuteStep?: number;
}): string | null {
  if (!(endMs > startMs)) return 'Return must be after pickup';

  if (slots.length > 0) {
    const bufferMs = bufferHours * 60 * 60 * 1000;
    const conflict = slots.find(
      slot => startMs < slot.endAt + bufferMs && endMs > slot.startAt - bufferMs,
    );
    if (conflict) {
      return `Conflicts with a booking ${formatInstant(conflict.startAt, timezone)} to ${formatInstant(conflict.endAt, timezone)}`;
    }
  }

  const startKey = dayKeyOf(startMs, timezone);
  const endKey = dayKeyOf(endMs, timezone);
  const blockedDays =
    blocked ??
    computeBlockedDateSets({
      slots,
      bufferHours,
      fromKey: startKey,
      toKey: endKey,
      timezone,
      minuteStep,
    }).blocked;
  const blockedInside = dayKeysBetween(startKey, endKey).find(key => blockedDays.has(key));
  if (blockedInside) {
    return `${DateTime.fromISO(blockedInside).toFormat('MMM d')} is fully unavailable`;
  }

  if (operatingHours && !operatingHours.alwaysAvailable) {
    const start = DateTime.fromMillis(startMs).setZone(timezone);
    const end = DateTime.fromMillis(endMs).setZone(timezone);
    const startMinute = start.hour * 60 + start.minute;
    const endMinute = end.hour * 60 + end.minute;
    if (
      outOfOperatingHoursMinutesFor(start.startOf('day'), operatingHours, minuteStep).has(
        startMinute,
      )
    ) {
      return 'Pickup time is outside operating hours';
    }
    if (
      outOfOperatingHoursMinutesFor(end.startOf('day'), operatingHours, minuteStep).has(endMinute)
    ) {
      return 'Return time is outside operating hours';
    }
  }

  return null;
}
